/* eslint-disable */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { getCurrentUsername } from "utils/auth";
import AuthService from "services/authService";
import {
  clearCollectionDraftInputBuffersForUser,
  getCollectionDraftInputBufferUserKey,
} from "utils/collectionDraftInputBuffer";
import { clearCollectionEditInputBuffersForUser } from "utils/collectionEditInputBuffer";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();
  const currentUsername = getCurrentUsername();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleLogout = async () => {
    const userKey = getCollectionDraftInputBufferUserKey();
    try {
      await AuthService.logout();
    } catch (err) {
      // Local cleanup must run even if the server logout request fails.
    } finally {
      if (userKey) clearCollectionDraftInputBuffersForUser({ userKey });
      if (userKey) clearCollectionEditInputBuffersForUser({ userKey });
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("username");
      navigate("/authentication/sign-in");
    }
  };

  const actionButtonStyle = ({ palette: { dark }, functions: { rgba } }) => ({
    minHeight: 40,
    minWidth: 0,
    px: { xs: 1, sm: 1.5 },
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
    textTransform: "none",
    lineHeight: 1.2,
    color: dark.main,
    borderColor: rgba(dark.main, 0.55),
    "&:hover": {
      color: dark.main,
      borderColor: dark.main,
      backgroundColor: rgba(dark.main, 0.06),
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox
            sx={(theme) => ({
              ...navbarRow(theme, { isMini }),
              width: "100%",
              justifyContent: "flex-end",
            })}
          >
            <MDBox
              color={light ? "white" : "inherit"}
              sx={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              {currentUsername && (
                <MDBox
                  component="span"
                  px={0}
                  py={0}
                  display={{ xs: "none", sm: "inline-flex" }}
                  alignItems="center"
                  minWidth={0}
                  flex="1 1 auto"
                  overflow="hidden"
                >
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    color="inherit"
                    noWrap
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    Usuario: {currentUsername}
                  </MDTypography>
                </MDBox>
              )}
              <Tooltip title="Cerrar sesión">
                <Button
                  variant="outlined"
                  size="small"
                  disableRipple
                  onClick={handleLogout}
                  sx={actionButtonStyle}
                >
                  Cerrar sesión
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                disableRipple
                onClick={handleMiniSidenav}
                sx={(theme) => ({
                  ...actionButtonStyle(theme),
                  ...navbarMobileMenu(theme),
                  display: "inline-flex",
                  lineHeight: 1.2,
                  [theme.breakpoints.up("xl")]: {
                    display: "none",
                  },
                })}
              >
                <MDBox component="span" display={{ xs: "inline", sm: "none" }}>
                  Menú
                </MDBox>
                <MDBox component="span" display={{ xs: "none", sm: "inline" }}>
                  {miniSidenav ? "Abrir menú" : "Cerrar menú"}
                </MDBox>
              </Button>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
